import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { showToast } from './Toast';
import api from '../api';

export default function NotificationBell() {
  const { token, socket } = useAuth();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    if (!token) return;
    const fetch = () => api.notificationsUnreadCount(token).then(d => setCount(d.count)).catch(() => {});
    fetch();
    const interval = setInterval(fetch, 60000); // 폴백: 60초
    return () => clearInterval(interval);
  }, [token]);

  // Socket-based real-time notification
  useEffect(() => {
    if (!socket) return;
    const handler = (data) => {
      setCount(prev => prev + 1);
      showToast(data.message || '새 알림이 도착했습니다.', 'info');
      // Browser Notification
      if (Notification.permission === 'granted') {
        new Notification('LunaMoon', { body: data.message || '새 알림', icon: '/logo192.png' });
      }
    };
    socket.on('notification', handler);
    socket.on('new_message_notification', (data) => {
      // Suppress notification if user is currently viewing this chat room
      const path = window.location.pathname;
      const match = path.match(/\/chat\/(\d+)/);
      if (match && parseInt(match[1]) === data.room_id) return;
      setCount(prev => prev + 1);
      showToast(`${data.sender_nickname}: ${data.preview}`, 'info');
    });
    return () => { socket.off('notification', handler); socket.off('new_message_notification'); };
  }, [socket]);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggle = async () => {
    if (!open && token) {
      try {
        const data = await api.notifications(token);
        setItems(data.notifications || []);
      } catch {}
    }
    setOpen(!open);
  };

  const markAllRead = async () => {
    if (!token) return;
    await api.notificationsMarkRead(null, token).catch(() => {});
    setCount(0);
    setItems(prev => prev.map(n => ({ ...n, is_read: true })));
  };

  if (!token) return null;

  return (
    <div ref={ref} className="relative">
      <button onClick={toggle} className="relative text-white/70 hover:text-white transition p-1">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {count > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-bold">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-10 w-80 bg-luna-dark border border-white/10 rounded-2xl shadow-2xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <span className="font-bold text-sm">알림</span>
            {count > 0 && (
              <button onClick={markAllRead} className="text-xs text-luna-violet hover:underline">모두 읽음</button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 ? (
              <div className="text-white/40 text-sm text-center py-8">알림이 없습니다</div>
            ) : items.slice(0, 10).map(n => {
              const data = typeof n.data === 'string' ? JSON.parse(n.data || '{}') : (n.data || {});
              const isBooking = n.type === 'new_booking' || n.type === 'booking_confirmed' || n.type === 'booking_cancelled';
              return (
                <div key={n.id} onClick={() => {
                  if (isBooking) { setOpen(false); navigate('/dashboard'); }
                }}
                  className={`px-4 py-3 border-b border-white/5 ${n.is_read ? '' : 'bg-luna-purple/5'} ${isBooking ? 'cursor-pointer hover:bg-white/5' : ''}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium flex-1">{n.title}</span>
                    {isBooking && <span className="text-[10px] px-1.5 py-0.5 rounded bg-luna-purple/20 text-luna-violet shrink-0">예약</span>}
                  </div>
                  {n.body && <div className="text-white/50 text-xs mt-1">{n.body}</div>}
                  <div className="text-white/30 text-xs mt-1">{n.created_at?.slice(0, 16)}</div>
                </div>
              );
            })}
          </div>
          <button onClick={() => { setOpen(false); navigate('/notifications'); }}
            className="w-full text-center py-3 text-luna-violet text-sm hover:bg-white/5 transition border-t border-white/10">
            전체 보기
          </button>
        </div>
      )}
    </div>
  );
}
